<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { LoadTypeDirection, type IWarehouse } from '@nova/core';

import { routeToAppointmentErrorScreen } from '@/composables/useApiErrorRouting';
import { useAppointmentLookup } from '@/composables/useAppointmentLookup';
import { CheckinFlowStep, useCheckinFlow } from '@/composables/useCheckinFlow';
import {
  getLoadTypeDirectionI18nKey,
  inferAssetVisitTypeFromLoadType
} from '@/utils/assetVisitType';

defineOptions({ name: 'WelcomePage' });

const props = defineProps<{
  warehouse: Partial<IWarehouse>;
}>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const checkinFlow = useCheckinFlow();

const warehouseId = computed(() => (route.query.warehouseId as string) ?? '');

const { identifier, isLookingUp, appointment, notFoundError, search } = useAppointmentLookup({
  warehouseId,
  onApiError: error => routeToAppointmentErrorScreen(router, warehouseId.value, error)
});

const referenceNumberInputMode = computed(() => props.warehouse.settings?.referenceNumberInputMode);

// The unplanned flow never collects a reference, so offering it would strand the driver on the
// details step, where the requirement is enforced but no field exists to satisfy it.
const showNoReferenceOption = computed(
  () => props.warehouse.settings?.gateManagementDriverReferenceRequired !== true
);

const isRefNumberRequired = computed(
  () => props.warehouse.settings?.gateManagementDriverReferenceRequired === true
);

const mustMatchAppointment = computed(
  () => props.warehouse.settings?.gateManagementDriverReferenceValidated === true
);

// When the reference must match an appointment there is no fallback path, so a value the backend
// already rejected can only be retried by editing it — which clears notFoundError.
const isCheckInDisabled = computed(
  () =>
    !identifier.value.trim() ||
    isLookingUp.value ||
    (mustMatchAppointment.value && notFoundError.value)
);

function getDirectionArrowIcon(direction: LoadTypeDirection): string {
  if (direction === LoadTypeDirection.Inbound) {
    return 'mdi-arrow-left-thin';
  }
  if (direction === LoadTypeDirection.Outbound) {
    return 'mdi-arrow-right-thin';
  }
  return 'mdi-swap-horizontal';
}

function getDirectionLabel(direction: LoadTypeDirection): string {
  const key = getLoadTypeDirectionI18nKey(direction);
  return key ? t(`initial.direction.${key}`) : direction;
}

async function handleCheckIn() {
  if (!appointment.value) {
    await search();
  }

  if (appointment.value) {
    checkinFlow.setAppointmentId(appointment.value.id);
    checkinFlow.setLoadTypeId(appointment.value.loadType?.id ?? null);
    checkinFlow.setAssetVisitType(inferAssetVisitTypeFromLoadType(appointment.value.loadType));
    checkinFlow.setIdentifier(identifier.value);
    checkinFlow.setAppointment(appointment.value);

    checkinFlow.setNextStep(CheckinFlowStep.CheckinDetailsPage);
    return;
  }

  if (isRefNumberRequired.value && !mustMatchAppointment.value) {
    startUnplannedCheckin(identifier.value);
  }
}

function startUnplannedCheckin(referenceNumber: string) {
  checkinFlow.setAppointmentId(null);
  checkinFlow.setAppointment(null);
  checkinFlow.setIdentifier(referenceNumber);
  checkinFlow.setNextStep(CheckinFlowStep.PurposeOfStop);
}

function handleNoReference() {
  startUnplannedCheckin(identifier.value);
}
</script>

<template>
  <div class="comet-i18n-exclude flex-grow-1 d-flex flex-column justify-space-between">
    <div class="d-flex flex-column gap-4">
      <p class="typography-heading-sm-bold">{{ t('initial.lookupTitle') }}</p>

      <v-text-field
        v-model="identifier"
        :label="t('initial.referenceNumberLabel')"
        :type="referenceNumberInputMode === 'numeric' ? 'number' : 'text'"
        :inputmode="referenceNumberInputMode === 'numeric' ? 'numeric' : 'text'"
        color="primary"
        variant="outlined"
        class="reference-number-field"
        data-testid="comet-reference-lookup"
        :error-messages="notFoundError ? t('initial.referenceNotFoundError') : ''"
        hide-details="auto">
        <template #append-inner>
          <v-progress-circular
            v-if="isLookingUp"
            indeterminate
            size="20"
            width="2"
            color="primary"></v-progress-circular>
        </template>
      </v-text-field>

      <v-card
        v-if="appointment"
        variant="outlined"
        class="pa-4 d-flex flex-column gap-3 load-type-details"
        data-testid="comet-appointment-details">
        <div v-if="appointment.loadType" class="d-flex gap-1">
          <span class="typography-body-md-bold">{{ t('initial.loadTypeLabel') }}:</span>
          <span class="typography-body-md">{{ appointment.loadType.name }}</span>
        </div>
        <div v-if="appointment.loadType" class="d-flex align-center gap-1">
          <span class="typography-body-md-bold">{{ t('initial.directionLabel') }}:</span>
          <span class="typography-body-md">
            {{ getDirectionLabel(appointment.loadType.direction) }}
          </span>
          <div class="d-flex align-center rounded-sm bg--color-primary-20 pa-1">
            <v-icon
              size="14"
              :icon="getDirectionArrowIcon(appointment.loadType.direction)"></v-icon>
            <v-icon size="14" icon="mdi-truck-outline"></v-icon>
          </div>
        </div>
        <div v-if="appointment.dock" class="d-flex gap-1">
          <span class="typography-body-md-bold">{{ t('initial.dockLabel') }}:</span>
          <span class="typography-body-md">{{ appointment.dock.name }}</span>
        </div>
      </v-card>

      <v-btn
        block
        append-icon="mdi-arrow-right"
        color="primary"
        :disabled="isCheckInDisabled"
        data-testid="comet-check-in"
        @click="handleCheckIn">
        {{ t('initial.checkIn') }}
      </v-btn>
    </div>

    <div v-if="showNoReferenceOption" class="d-flex flex-column gap-4">
      <div class="d-flex align-center gap-2">
        <v-divider></v-divider>
        <span class="typography-body-sm">{{ t('initial.orDivider') }}</span>
        <v-divider></v-divider>
      </div>

      <v-btn
        variant="outlined"
        block
        class="no-reference-btn pa-4 bg--color-neutral-10 border--color-neutral-40"
        data-testid="comet-no-reference"
        @click="handleNoReference">
        <div class="d-flex flex-column gap-1 text-left">
          <span class="typography-body-md-bold">{{ t('initial.noReferenceTitle') }}</span>
          <span class="typography-body-sm">{{ t('initial.noReferenceDescription') }}</span>
        </div>
        <v-icon>mdi-arrow-right</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
.reference-number-field :deep(.v-input__details) {
  padding-left: 0;
}

.load-type-details {
  background-color: rgba($color-primary-20, 0.3) !important;
  border-color: $color-primary-20 !important;
}

.no-reference-btn {
  height: auto !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  border-radius: 8px;

  :deep(.v-btn__content) {
    width: 100%;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    white-space: normal;
  }
}
</style>
